import type React from "react";

export type ClaimLandingApiResponse = Record<string, unknown> | null;

export const COMPONENT_ID_TO_CARD_NAME: Record<string, string> = {
  COMPONENT_CLAIMS_RAISE_NEW_CLAIM_CARD: "RaiseNewClaimCard",
  COMPONENT_CLAIMS_FAQ_CARD: "FAQCard",
  COMPONENT_CLAIMS_STATUS_CARD: "ClaimStatusCard",
};

export interface ApiComponentEntry {
  id?: string;
  data?: unknown;
}

export type ClaimCardBlock = {
  cardName?: string;
  data?: unknown;
};

export interface ClaimPageData {
  bookingId?: string;
  currency?: string;
}

export type ClaimLandingNormalized = {
  headingTitle: string;
  pageData: ClaimPageData;
  cardList: ClaimCardBlock[];
  byCardName: Record<string, unknown>;
  raw: unknown;
};

export type ClaimsWrapperProps = {
  data: ClaimLandingApiResponse;
};

export type ClaimsDesktopProps = {
  normalizedData: ClaimLandingNormalized;
};

export type ClaimsPWAProps = {
  normalizedData: ClaimLandingNormalized;
};

export type ClaimContentType = "staticPage" | "claimFlow";

export type ClaimViewState = "landing" | "info";

export interface ClaimInfoViewParams {
  detailFetchParams: ClaimDetailFetchParams;
  categoryTitle?: string;
  categorySubtitle?: string;
  categoryIcon?: string;
  contentType: ClaimContentType;
}

export type ClaimsContextValue = ClaimLandingNormalized & {
  refreshClaimData: () => void;
  activeView: ClaimViewState;
  infoViewParams: ClaimInfoViewParams | null;
  navigateToInfo: (params: ClaimInfoViewParams) => void;
  navigateToLanding: () => void;
};

export interface ClaimsCardItem {
  cardName: string;
  data: unknown;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ClaimsComponentMapperType = Record<
  string,
  Record<string, React.ComponentType<any>>
>;

export interface ClaimsComponentRendererProps {
  cards?: ClaimsCardItem[];
  layout: "left" | "right" | "top" | "rest";
  mapper?: ClaimsComponentMapperType;
}

export interface TextNode {
  text?: {
    value?: string;
    styles?: Record<string, string>;
  };
}

export interface ClaimedCardStyle {
  backgroundColor?: string;
  cornerRadius?: string;
  titleColor?: string;
  claimTypeTitleColor?: string;
  bottomBorderColor?: string | string[];
}

export interface ClaimedCardTraveller {
  name?: string;
  gender?: string;
  age?: string;
}

export interface ViewDetailData {
  title?: TextNode;
  travellers?: ClaimedCardTraveller[];
}

export interface ClaimedCardAction {
  actionId?: string;
  title?: TextNode;
  titleColor?: string;
  icon?: { url?: string; size?: { width?: number; height?: number } };
  viewDetailData?: ViewDetailData;
  type?: string;
  url?: string;
  openInWebView?: boolean;
}

export interface ClaimDetailStatusInfo {
  title?: TextNode;
  description?: TextNode;
  footnote?: TextNode;
}

export interface ClaimDetailItem {
  key?: string;
  label?: TextNode;
  value?: TextNode;
}

export interface ClaimDetailGroup {
  items?: ClaimDetailItem[];
}

export interface ClaimDetailDocument {
  documentId?: string;
  title?: TextNode;
  key?: string;
  fileName?: string;
  fileExtension?: string;
  category?: string;
}

export interface ClaimDetailDocumentsGroup {
  label?: TextNode;
  items?: ClaimDetailDocument[];
}

export interface ClaimDetailSheet {
  title?: TextNode;
  status?: ClaimDetailStatusInfo;
  claimInfo?: ClaimDetailGroup;
  formData?: ClaimDetailGroup;
  documents?: ClaimDetailDocumentsGroup;
}

export interface ClaimedCard {
  state?: string;
  title?: TextNode;
  claimTypeTitle?: TextNode;
  icon?: string;
  style?: ClaimedCardStyle;
  action?: ClaimedCardAction;
  detailSheet?: ClaimDetailSheet;
}

export interface ClaimStatusCardProps {
  title?: TextNode;
  noClaimsTitle?: TextNode;
  claimedCards?: ClaimedCard[];
}

export interface ClaimDetailFetchParams {
  categoryId?: string;
  nodeId?: string;
}

export interface ClaimReasonNode {
  id?: string;
  title?: TextNode;
  subtitle?: TextNode;
  detailFetchParams?: ClaimDetailFetchParams;
  contentType?: ClaimContentType;
  isDisabled?: boolean;
}

export interface ClaimReasonData {
  title?: TextNode;
  nodes?: ClaimReasonNode[];
}

export interface ClaimCategory {
  id?: string;
  type?: "static" | "separateNode" | string;
  title?: TextNode;
  icon?: string;
  showChevron?: boolean;
  contentType?: ClaimContentType;
  detailFetchParams?: ClaimDetailFetchParams;
  actionId?: string;
  reasonData?: ClaimReasonData;
  isDisabled?: boolean;
}

export interface RaiseNewClaimPolicyCard {
  provider?: TextNode;
  route?: TextNode;
  travelDates?: TextNode;
  pax?: TextNode;
  viewDetailData?: ViewDetailData;
  style?: {
    backgroundColor?: string;
    cornerRadius?: string;
  };
}

export interface RaiseNewClaimCardProps {
  title?: TextNode;
  policyCard?: RaiseNewClaimPolicyCard;
  claimPrompt?: TextNode;
  claimCategories?: ClaimCategory[];
  disclaimerBox?: {
    message?: TextNode;
    style?: { backgroundColor?: string };
  };
}

export interface StaticPageSectionItem {
  name?: TextNode;
  description?: TextNode;
}

export interface StaticPageSection {
  id?: number;
  title?: TextNode;
  items?: StaticPageSectionItem[];
}

export interface ClaimCategoryDetailsData {
  iconUrl?: string;
  title?: TextNode;
  subtitle?: TextNode;
  sections?: StaticPageSection[];
}

export interface HelpContactCardData {
  title?: TextNode;
  description?: TextNode;
  contactInfo?: { text?: string };
  backgroundColor?: string;
}

export interface StaticPageBodyComponent {
  cardName?: string;
  data?: Record<string, unknown>;
}

export interface ClaimStaticPageData {
  headerTitle?: string;
  bodyComponents?: StaticPageBodyComponent[];
  previousCta?: ClaimFormCta;
}

export interface ClaimInfoViewProps {
  params: ClaimInfoViewParams;
  bookingId: string;
  partnerBookingId?: string;
  currency: string;
  onBack: () => void;
}

export const CLAIM_FLOW_CARD = {
  CLAIM_REQUEST: "ClaimRequestCard",
  ADD_DETAILS_FORM: "AddDetailsClaimFormCard",
} as const;

export type ClaimFieldType = "text" | "datePicker" | "fileUploader";

export interface ClaimDateConstraint {
  value?: string;
  message?: string;
}

export interface ClaimValidationRule {
  type?: string;
  value?: unknown;
  errorMessage?: string;
}

export interface ClaimFieldValidation {
  regex?: string;
  errorMessage?: string;
  rules?: ClaimValidationRule[];
  minDate?: ClaimDateConstraint;
  maxDate?: ClaimDateConstraint;
}

export interface ClaimFileUpload {
  allowedFileTypes?: string[];
  maxFileSizeMB?: number;
  ctaText?: TextNode;
}

export interface ClaimFormField {
  key?: string;
  type?: ClaimFieldType | string;
  label?: TextNode;
  subLabel?: TextNode;
  placeholder?: string;
  isMandatory?: boolean;
  autoFillIfPreSelected?: boolean;
  value?: string;
  readOnly?: boolean;
  prefix?: string;
  tooltip?: TextNode;
  validation?: ClaimFieldValidation;
  fileUpload?: ClaimFileUpload;
  styles?: Record<string, string>;
}

export interface ClaimFormFooterNote {
  icon?: string;
  message?: TextNode;
  style?: Record<string, string>;
}

export interface ClaimFormCta {
  text?: TextNode;
}

export type ClaimFormHeaderTitle =
  | string
  | TextNode
  | {
      value?: string;
      styles?: Record<string, string>;
      text?: TextNode["text"];
    };

export interface AddDetailsClaimFormCardData {
  headerTitle?: ClaimFormHeaderTitle;
  claimDetailsSection?: { fields?: ClaimFormField[] };
  footerNote?: ClaimFormFooterNote;
  previousCta?: ClaimFormCta;
  submitCta?: ClaimFormCta;
}

export interface RichTextSpan {
  value?: string;
  styles?: Record<string, string>;
}

export interface ClaimEligibilityCriteriaItem {
  id?: number;
  name?: TextNode;
  description?: TextNode;
}

export interface ClaimEligibilityCta {
  text?: TextNode;
  action?: {
    data?: {
      title?: TextNode;
      criteria?: ClaimEligibilityCriteriaItem[];
      primaryCta?: TextNode;
    };
  };
}

export interface ClaimRequestCardData {
  title?: { text?: RichTextSpan[] };
  subText?: TextNode;
  paxInfo?: { text?: TextNode; iconUrl?: string };
  iconUrl?: string;
  checkEligibilityCta?: ClaimEligibilityCta;
}

export interface ClaimFlowCardItem {
  cardName?: string;
  data?: Record<string, unknown>;
}

export interface ClaimFlowData {
  headerTitle?: string;
  benefitKey?: string;
  cardCategory?: ClaimFlowCardItem[];
}

export const CLAIM_RESULT_COMPONENT_ID = "COMPONENT_CLAIMS_SUBMIT_RESULT";

export const CLAIM_ACTION = {
  RAISE_CLAIM: "RAISE_CLAIM",
  TRY_AGAIN: "TRY_AGAIN",
} as const;

export interface ClaimResultCta {
  actionId?: string;
  text?: string | TextNode;
}

export interface ClaimResultReferenceNumber {
  label?: string | TextNode;
  value?: string | TextNode;
}

export interface ClaimResultClaimCard {
  title?: string | TextNode;
  icon?: string;
  claimType?: { label?: string | TextNode; value?: string | TextNode };
  paxInfo?: string | TextNode;
}

export interface ClaimResultVariant {
  animationUrl?: string;
  title?: string | TextNode;
  subtitle?: string | TextNode;
  referenceNumber?: ClaimResultReferenceNumber;
  claimCard?: ClaimResultClaimCard;
  refundMessage?: string | TextNode;
  primaryCta?: ClaimResultCta;
  secondaryCta?: ClaimResultCta;
}

export interface ClaimSubmitResultData {
  success?: ClaimResultVariant;
  failure?: ClaimResultVariant;
}

export type ClaimFormViewProps = ClaimInfoViewProps;
